/* eslint-disable import/no-cycle */
import { RouteRecordRaw } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { IRouteConfigMeta, IRouteConfigAuthMeta, IRouteMetaPermissionConfig, ERouteMetaPermissionMode } from '@/shared';
import { hideFromNavChildClient } from '@/shared/helpers/hide-nav-from-child-client';
import app from '@/main';

const getAuthByProduct = (perms: Partial<IRouteMetaPermissionConfig>[], condition?: 'and' | 'or'): IRouteConfigAuthMeta => {
  const permConfigs: IRouteMetaPermissionConfig[] = [];
  perms.forEach((perm) => {
    if (perm.key) {
      permConfigs.push({
        key: perm.key,
        keyType: perm.keyType ?? ModelType.building,
        idParam: perm.idParam ?? 'propertyId',
        checkAtLeastOneModel: perm.checkAtLeastOneModel,
        accessType: perm.accessType ?? 'read',
      });
    }
  });
  return {
    required: true,
    permissions: permConfigs,
    condition,
  };
};

const propertyDetailsBreadcrumbs: IRouteConfigMeta['breadcrumbs'] = (vm, disabled: boolean = false) => {
  const propertyTitle = vm?.$utils?.decodeHtml(vm?.$rsGlobals.$sidebarHeaderData?.data?.buildingName) ?? '';
  const propertyId = vm?.$route.params.propertyId;

  return [
    {
      title: 'Properties',
      to: '/properties',
    },
    {
      title: propertyTitle,
      to: `/properties/${propertyId}/details`,
      disabled,
    },
  ];
};

const unitDetailsBreadcrumbs: IRouteConfigMeta['breadcrumbs'] = (vm, disabled: boolean = false) => {
  const propertyId = vm?.$route.params.propertyId;

  return [
    ...propertyDetailsBreadcrumbs(vm),
    {
      title: 'Units',
      to: `/properties/${propertyId}/unit-types`,
      disabled,
    },
  ];
};

const propertyList: RouteRecordRaw = {
  path: '/properties',
  name: 'PropertyList',
  component: () => import('@/views/properties/Index.vue'),
  meta: {
    auth: getAuthByProduct([
      'buildings',
      'listing_scores',
      'units',
      'quick_update',
      'promotions',
      'building_photos',
      'maintenance',
      'pages',
      'standard_listings',
    ].map((perm) => ({ key: perm, checkAtLeastOneModel: true })), 'or'),
    title: 'Properties',
    icon: 'house',
    parent: null,
    active: false,
    noPagePadding: true,
  },
};

const addNewProperty: RouteRecordRaw = {
  path: '/properties/new',
  name: 'AddNewProperty',
  component: () => import('@/views/properties/single/new/Index.vue'),
  meta: {
    auth: getAuthByProduct([
      'buildings',
    ].map((perm) => ({ key: perm, checkAtLeastOneModel: true, accessType: 'create' })), 'or'),
    title: 'Properties',
    icon: 'house',
    parent: null,
    active: false,
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },
};

const listingScores: RouteRecordRaw = {
  path: '/properties/listing-scores',
  name: 'ListingScoreGlobal',
  component: () => import('@/views/properties/ListingScores.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'listing_scores', checkAtLeastOneModel: true },
    ]),
    parent: null,
    title: 'Listing Scores',
    icon: 'heartbeat',
    active: false,
    noPagePadding: true,
    infoUrl: '#',
    reportingUrl: '/reporting/listing_scores',
    breadcrumbs: (vm) => ([
      {
        to: '/properties',
        title: 'Properties',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
    disabled: () => {
      const vm = app();
      if (vm) return vm.$rsGlobals.$isListingScoresDisabled ? { tooltip: 'Contact your Rentsync account manager to enable this feature.' } : undefined;
      return undefined;
    },
  },
};

const propertyPromotions: RouteRecordRaw = {
  path: '/properties/promotions',
  name: 'PropertyPromotions',
  component: () => import('@/views/properties/promotions/Index.vue'),
  meta: {
    parent: null,
    auth: getAuthByProduct([
      { key: 'promotions', checkAtLeastOneModel: true },
    ]),
    infoUrl: 'y',
    title: 'Promotions',
    active: false,
    icon: 'badge-dollar',
    noPagePadding: true,
    breadcrumbs: (vm) => ([
      {
        to: '/properties',
        title: 'Properties',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const propertyPromotionsDetails: RouteRecordRaw = {
  path: '/properties/promotions/:promotionId/details',
  name: 'PropertyPromotionDetails',
  component: () => import('@/views/properties/promotions/single/Index.vue'),
  meta: {
    parent: propertyPromotions,
    auth: getAuthByProduct([
      { key: 'promotions', checkAtLeastOneModel: true },
    ]),
    infoUrl: 'y',
    title: 'Promotion Details',
    active: false,
    icon: 'badge-dollar',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
      hasTranslationsBtn: () => true,
      extraButtons: {
        promotionDuplicate: {
          buttonText: () => app()?.$ts('promotions.single.btnDuplicate') ?? 'Duplicate Promotion',
          buttonProps: {
            hollow: true,
            variant: 'primary',
          },
        },
      },
    },
    breadcrumbs: (vm) => ([
      {
        to: '/properties',
        title: 'Properties',
      },
      {
        to: '/properties/promotions',
        title: 'Promotions',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
    iframeBackButtonTarget: () => propertyPromotions,
  },
};

const propertyUnitModels: RouteRecordRaw = {
  path: '/properties/unit-types',
  name: 'PropertyModels',
  component: () => import('@/views/properties/UnitModels.vue'),
  meta: {
    parent: null,
    auth: getAuthByProduct([
      { key: 'units', checkAtLeastOneModel: true },
    ]),
    title: 'All Units',
    dynamicHideFromNav: hideFromNavChildClient,
    dynamicTitle: () => 'Units',
    noPagePadding: true,
    active: false,
    icon: 'house',
    breadcrumbs: (vm) => ([
      {
        to: '/properties',
        title: 'Properties',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const quickUpdateMeta = {
  disabled: () => {
    const vm = app();
    if (vm) return vm.$rsGlobals.$isQuickUpdateDisabled ? { tooltip: 'Contact your Rentsync account manager to enable this feature.' } : undefined;
    return undefined;
  },
  auth: getAuthByProduct([
    { key: 'quick_update', checkAtLeastOneModel: true },
  ]),
  parent: null,
  title: 'Quick Update',
  active: false,
  icon: 'hammer',
  noPagePadding: true,
  infoUrl: 'y',
  footerBar: {
    enabled: true,
    hasSave: true,
    hasTranslations: true,
  },
};

const quickUpdateBreadCrumbs = (vm: ReturnType<typeof app>, includeTracking?: boolean) => [
  {
    title: 'Properties',
    to: '/properties',
  },
  {
    title: 'Quick Update',
    to: '/properties/quick-updates/rates-availability',
  },
  ...includeTracking ? [{
    title: 'Custom Tracking',
    to: '/properties/quick-updates/custom-tracking-url',
  }] : [],
  {
    title: vm?.$route.meta?.title ?? '',
    to: vm?.$route.path ?? '',
  },
];

const quickUpdateIndex: RouteRecordRaw = {
  path: '/properties/quick-updates',
  name: 'QuickUpdateIndex',
  component: () => import('@/views/properties/quick-updates/QuickUpdateIndex.vue'),
  children: [
    {
      path: 'rates-availability',
      component: () => import('@/views/properties/quick-updates/RsRatesAvailabilityQuickUpdate.vue'),
      meta: {
        ...quickUpdateMeta,
        title: 'Rent & Availability',
        breadcrumbs: quickUpdateBreadCrumbs,
        auth: getAuthByProduct([
          { key: 'quick_update', checkAtLeastOneModel: true, mode: ERouteMetaPermissionMode.AT_LEAST_ONE, accessType: 'update' },
          { key: 'units', checkAtLeastOneModel: true, mode: ERouteMetaPermissionMode.AT_LEAST_ONE, accessType: 'update' },
        ], 'and'),
      },
    },
    {
      path: 'promotions-quick',
      component: () => import('@/views/properties/quick-updates/RsPromotionsQuickUpdate.vue'),
      meta: {
        ...quickUpdateMeta,
        auth: getAuthByProduct([
          { key: 'promotions', checkAtLeastOneModel: true },
        ]),
        title: 'Promotions',
        breadcrumbs: quickUpdateBreadCrumbs,
      },
    },
    {
      path: 'contact-info',
      component: () => import('@/views/properties/quick-updates/RsContactInfoQuickUpdate.vue'),
      meta: { ...quickUpdateMeta, title: 'Contact Info', breadcrumbs: quickUpdateBreadCrumbs },
    },
    {
      path: 'custom-tracking-url',
      component: () => import('@/views/properties/quick-updates/CustomTrackingQuickUpdate.vue'),
      meta: {
        ...quickUpdateMeta,
        auth: getAuthByProduct([
          { key: 'quick_update', checkAtLeastOneModel: true },
          { key: 'standard_listings', checkAtLeastOneModel: true },
        ]),
        footerBar: { ...quickUpdateMeta.footerBar, hasTranslations: false },
        title: 'Custom URLs',
        breadcrumbs: (vm) => [
          ...quickUpdateBreadCrumbs(app(), true),
        ],
      },
      props: { rentSyncTrackingField: 'custom_url' },
    },
    {
      path: 'custom-tracking-email',
      component: () => import('@/views/properties/quick-updates/CustomTrackingQuickUpdate.vue'),
      meta: {
        ...quickUpdateMeta,
        auth: getAuthByProduct([
          { key: 'quick_update', checkAtLeastOneModel: true },
          { key: 'standard_listings', checkAtLeastOneModel: true },
        ]),
        footerBar: { ...quickUpdateMeta.footerBar, hasTranslations: false },
        title: 'Custom Emails',
        breadcrumbs: (vm) => [
          ...quickUpdateBreadCrumbs(app(), true),
        ],
      },
      props: { rentSyncTrackingField: 'building_email' },
    },
    {
      path: 'custom-tracking-phone',
      component: () => import('@/views/properties/quick-updates/CustomTrackingQuickUpdate.vue'),
      meta: {
        ...quickUpdateMeta,
        auth: getAuthByProduct([
          { key: 'quick_update', checkAtLeastOneModel: true },
          { key: 'standard_listings', checkAtLeastOneModel: true },
        ]),
        footerBar: { ...quickUpdateMeta.footerBar, hasTranslations: false },
        title: 'Custom Phone Numbers',
        breadcrumbs: (vm) => [
          ...quickUpdateBreadCrumbs(app(), true),
        ],
      },
      props: { rentSyncTrackingField: 'building_phone' },
    },
  ],
  meta: quickUpdateMeta,

};

const propertyMaintenanceRequests: RouteRecordRaw = {
  path: '/properties/maintenance-requests',
  name: 'PropertyMaintenanceRequests',
  component: () => import('@/views/properties/MaintenanceRequests.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'maintenance', checkAtLeastOneModel: true },
    ]),
    parent: null,
    title: 'Maintenance Requests',
    active: false,
    icon: 'hammer',
    noPagePadding: true,
    breadcrumbs: (vm) => ([
      {
        to: '/properties',
        title: 'Properties',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

/**
 * The router is a redirection target for propertySingleDetails.
 * The reason we do this ugly thing is because we want user to be redirected to other pages,
 * but stay on the property details page if the user does have the Property Management permission.
 * However, the "redirect" property cannot return the same page's url or it will cause an infinite loop in route handler.
 * So we have this shit to make it work.
 * But it needs better naming. It should be called .../details.
 * But Jeremy doesn't want to take this mass of work for now.
 * Now the routes propertySingle and propertySingleDetails are just twisted.
 */
export const propertySingle: RouteRecordRaw = {
  path: '/properties/:propertyId/single',
  name: 'PropertySingle',
  component: () => import('@/views/properties/single/Index.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'buildings' },
    ]),
    parent: propertyList,
    title: 'Property Details',
    active: false,
    hidePageTitle: true,
    noPagePadding: false,
    infoUrl: '#',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm, true),
      {
        to: `/properties/${vm?.$route.params.propertyId}/details`,
        title: 'Property Details',
        disabled: true,
      },
    ],
  },
};

export const propertySingleDetails: RouteRecordRaw = {
  path: '/properties/:propertyId/details',
  name: 'propertySingleDetails',
  redirect: (vm) => {
    try {
      const inst = app();

      // check if the user has details page permission, stay. Or app is not defined, stay.
      if (!inst || inst?.$rsGlobals.$userHasAccess('buildings', 'read', `${vm.params.propertyId}`)) {
        return `/properties/${vm.params.propertyId}/single`;
      }
      if (inst?.$rsGlobals.$userHasAccess('building_photos', 'read', `${vm.params.propertyId}`)) {
        return `/properties/${vm.params.propertyId}/media`;
      }
      if (inst?.$rsGlobals.$userHasAccess('units', 'read', `${vm.params.propertyId}`)) {
        return `/properties/${vm.params.propertyId}/unit-types`;
      }
      if (inst?.$rsGlobals.$currentUser.hasAtLeastOneModel('pages')) {
        return `/properties/${vm.params.propertyId}/pages`;
      }
      if (inst?.$rsGlobals.$userHasAccess('standard_listings', 'read', `${vm.params.propertyId}`)) {
        return `/properties/${vm.params.propertyId}/custom-tracking`;
      }
      return '/unauthorized';
    } catch (e) {
      console.warn(e);
      return `/properties/${vm.params.propertyId}/single`;
    }
  },
  meta: {
    auth: getAuthByProduct([
      { key: 'buildings' },
    ]),
    parent: propertyList,
    title: 'Property Details',
    active: false,
    hidePageTitle: true,
    noPagePadding: false,
    infoUrl: '#',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm, true),
      {
        to: `/properties/${vm?.$route.params.propertyId}/details`,
        title: 'Property Details',
        disabled: true,
      },
    ],
  },
};

const propertySingleModels: RouteRecordRaw = {
  path: '/properties/:propertyId/unit-types',
  name: 'PropertySingleModels',
  component: () => import('@/views/properties/single/UnitModels.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'units' },
    ]),
    backButtonTarget: () => ({
      handler: () => app()?.$router.back(), isCustom: true,
    }),
    parent: propertyList,
    title: 'Units',
    icon: 'house',
    active: false,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      ...unitDetailsBreadcrumbs(vm, true),
    ],
  },

};

const propertySingleUnits: RouteRecordRaw = {
  path: '/properties/:propertyId/unit-types/:unitId/details',
  name: 'PropertySingleMod',
  component: () => import('@/views/properties/single/UnitDetail.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'units' },
    ]),
    backButtonTarget: () => ({
      handler: () => app()?.$router.back(), isCustom: true,
    }),
    parent: propertySingleModels,
    title: 'Unit Details',
    icon: 'house',
    active: false,
    noPagePadding: false,
    infoUrl: '#',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
    breadcrumbs: (vm) => [
      ...unitDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData?.data?.suiteTypes[0]?.typeName ?? '',
        disabled: true,
      },
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/details`,
        title: 'Details',
        disabled: true,
      },
    ],
  },
};

const propertyUnitSingleCustomFields: RouteRecordRaw = {
  path: '/properties/:propertyId/unit-types/:unitId/custom-fields',
  name: 'PropertyUnitSingleCustomFields',
  component: () => import('@/views/properties/single/UnitCustomFields.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'units' },
    ]),
    backButtonTarget: () => ({
      handler: () => app()?.$router.back(), isCustom: true,
    }),
    footerBar: {
      enabled: true,
    },
    parent: propertySingleModels,
    title: 'Custom Fields',
    icon: 'house',
    active: false,
    noPagePadding: false,
    breadcrumbs: (vm) => [
      ...unitDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData?.data?.suiteTypes[0]?.typeName ?? '',
      },
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/custom-fields`,
        title: 'Custom Fields',
        disabled: true,
      },
    ],
    hideIframeBackButton: true,
  },
};

const propertySingleUnitsMedia: RouteRecordRaw = {
  path: '/properties/:propertyId/unit-types/:unitId/media',
  name: 'ProeprtyUnitMedia',
  component: () => import('@/views/properties/single/UnitMedia.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'units' },
      { key: 'building_photos' },
    ]),
    backButtonTarget: () => ({
      handler: () => app()?.$router.back(), isCustom: true,
    }),
    parent: propertySingleModels,
    title: 'Unit Media',
    icon: 'house',
    active: false,
    noPagePadding: false,
    infoUrl: '#',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    breadcrumbs: (vm) => [
      ...unitDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData?.data?.suiteTypes[0]?.typeName ?? '',
      },
      {
        to: `/properties/${vm?.$route.params.propertyId}/unit-types/${vm?.$route.params.unitId}/media`,
        title: 'Unit Media',
        disabled: true,
      },
    ],
  },
};

const propertyAmenities: RouteRecordRaw = {
  path: '/properties/:propertyId/amenities',
  name: 'PropertyAmenities',
  component: () => import('@/views/properties/single/Amenities.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'buildings' },
    ]),
    parent: propertyList,
    title: 'Amenities',
    active: false,
    hidePageTitle: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/amenities`,
        title: 'Amenities',
        disabled: true,
      },
    ],
  },
};

const propertyMedia: RouteRecordRaw = {
  path: '/properties/:propertyId/media',
  name: 'PropertyMedia',
  component: () => import('@/views/properties/single/Media.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'building_photos' },
    ]),
    parent: propertyList,
    title: 'Property Media',
    active: false,
    hidePageTitle: true,
    infoUrl: '#',
    footerBar: {
      enabled: true,
      hasSave: true,
      hasBottomText: true,
    },
    backButtonTarget: () => ({
      handler: () => app()?.$router.back(), isCustom: true,
    }),
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/media`,
        title: 'Property Media',
        disabled: true,
      },
    ],
  },
};

const propertySinglePages: RouteRecordRaw = {
  path: '/properties/:propertyId/pages',
  name: 'PropertySinglePages',
  component: () => import('@/views/properties/single/Pages.vue'),
  meta: {
    auth: {
      permissions: [
        { keyType: ModelType.site, key: 'pages', checkAtLeastOneModel: true },
      ],
    },
    title: 'Pages',
    icon: 'file-alt',
    hidePageTitle: true,
    noPagePadding: true,
    active: false,
    parent: propertyList,
    dynamicHideFromNav: () => {
      const vm = app();
      if (vm && vm.$rsGlobals.$currentUser.getClientSettingValue('buildings_pages') === 'on') {
        return false;
      }
      return true;
    },
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/pages`,
        title: 'Pages',
        disabled: true,
      },
    ],
  },
};

const propertySingleCustomContent: RouteRecordRaw = {
  path: '/properties/:propertyId/custom-content',
  name: 'PropertySingleCustomContent',
  component: () => import('@/views/properties/single/CustomContent.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'buildings' },
    ]),
    parent: propertyList,
    title: 'Custom Content',
    icon: 'toolbox',
    active: false,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/custom-content`,
        title: 'Custom Content',
        disabled: true,
      },
    ],
    hideIframeBackButton: true,
  },

};

const propertyCustomTracking: RouteRecordRaw = {
  path: '/properties/:propertyId/custom-tracking',
  name: 'PropertyCustomTracking',
  component: () => import('@/views/properties/CustomTracking.vue'),
  meta: {
    auth: getAuthByProduct([
      { key: 'standard_listings' },
    ]),
    active: false,
    noPagePadding: true,
    infoUrl: 'y',
    title: 'Custom Tracking',
    parent: propertyList,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: false,
    },
    breadcrumbs: (vm) => [
      ...propertyDetailsBreadcrumbs(vm),
      {
        to: `/properties/${vm?.$route.params.propertyId}/custom-tracking`,
        title: 'Custom Tracking',
        disabled: true,
      },
    ],
  },
};

export default [
  addNewProperty,
  propertyList,
  propertyUnitModels,
  propertyPromotions,
  propertyPromotionsDetails,
  quickUpdateIndex,
  propertyMaintenanceRequests,
  propertySingle,
  propertySingleDetails,
  listingScores,
  propertySingleUnits,
  propertySingleModels,
  propertyAmenities,
  propertyMedia,
  propertySingleCustomContent,
  propertySinglePages,
  propertySingleUnitsMedia,
  propertyUnitSingleCustomFields,
  propertyCustomTracking,
] as Array<RouteRecordRaw>;
